import React, { useState, useEffect } from 'react';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';

function MonthComparisonView({ user, selectedYear, month1, month2, onMonthChange, months }) {
  const [comparisonData, setComparisonData] = useState({ income: [], expense: [], savings: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadComparisonData();
  }, [month1, month2, selectedYear]);

  const loadComparisonData = async () => {
    setLoading(true);
    
    try {
      // Load categories
      const incomeSnapshot = await getDocs(collection(db, 'users', user.uid, 'income_categories'));
      const expenseSnapshot = await getDocs(collection(db, 'users', user.uid, 'expense_categories'));
      
      const incomeCategories = incomeSnapshot.docs.map(doc => doc.data().name?.trim()).filter(n => n).sort();
      const expenseCategories = expenseSnapshot.docs.map(doc => doc.data().name?.trim()).filter(n => n).sort();
      
      // Load data for both months
      const month1Str = String(month1 + 1).padStart(2, '0');
      const month2Str = String(month2 + 1).padStart(2, '0');
      const budgetDoc1Id = `budget_${selectedYear}_${month1Str}`;
      const budgetDoc2Id = `budget_${selectedYear}_${month2Str}`;
      
      const [doc1Snapshot, doc2Snapshot] = await Promise.all([
        getDoc(doc(db, 'users', user.uid, 'Budgets', budgetDoc1Id)),
        getDoc(doc(db, 'users', user.uid, 'Budgets', budgetDoc2Id))
      ]);
      
      const doc1Data = doc1Snapshot.exists() ? doc1Snapshot.data() : {};
      const doc2Data = doc2Snapshot.exists() ? doc2Snapshot.data() : {};
      
      // Build income comparison data
      const incomeData = incomeCategories.map(category => {
        const month1Actual = Math.abs(doc1Data.income?.[category]?.actual || 0);
        const month2Actual = Math.abs(doc2Data.income?.[category]?.actual || 0);
        
        // Handle division by zero
        let variance = 0;
        if (month1Actual === 0 && month2Actual === 0) {
          variance = 0;
        } else if (month1Actual === 0) {
          variance = 100; // Or could be Infinity, but 100% makes more sense
        } else {
          variance = ((month2Actual - month1Actual) / month1Actual) * 100;
        }
        
        return {
          category,
          month1Actual,
          month2Actual,
          variance
        };
      });
      
      // Build expense comparison data
      const expenseData = expenseCategories.map(category => {
        const month1Actual = Math.abs(doc1Data.expense?.[category]?.actual || 0);
        const month2Actual = Math.abs(doc2Data.expense?.[category]?.actual || 0);
        
        // Handle division by zero
        let variance = 0;
        if (month1Actual === 0 && month2Actual === 0) {
          variance = 0;
        } else if (month1Actual === 0) {
          variance = 100; // Or could be Infinity, but 100% makes more sense
        } else {
          variance = ((month2Actual - month1Actual) / month1Actual) * 100;
        }
        
        return {
          category,
          month1Actual,
          month2Actual,
          variance
        };
      });
      
      setComparisonData({
        income: incomeData,
        expense: expenseData,
        savings: []
      });
      
    } catch (error) {
      console.error('Error loading comparison data:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderComparisonTable = (data, title, colorClass) => {
    if (data.length === 0) return null;
    
    const month1Total = data.reduce((sum, item) => sum + Math.abs(item.month1Actual), 0);
    const month2Total = data.reduce((sum, item) => sum + Math.abs(item.month2Actual), 0);
    const totalVariance = month1Total !== 0 ? ((month2Total - month1Total) / month1Total) * 100 : 0;
    
    // For expenses, decrease is good (green), increase is bad (red)
    // For income, increase is good (green), decrease is bad (red)
    const isExpense = colorClass === 'expense';
    
    return (
      <div className="comparison-section">
        <h3 className={`section-title ${colorClass}`}>{title}</h3>
        <table className="comparison-table">
          <thead>
            <tr>
              <th>CATEGORY</th>
              <th className="right">{selectedYear}-{String(month1 + 1).padStart(2, '0')}</th>
              <th className="right">{selectedYear}-{String(month2 + 1).padStart(2, '0')}</th>
              <th className="center">% VAR</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, index) => {
              // Invert colors for expenses
              const isGood = isExpense ? item.variance < 0 : item.variance > 0;
              const isBad = isExpense ? item.variance > 0 : item.variance < 0;
              
              return (
                <tr key={index}>
                  <td><strong>{item.category}</strong></td>
                  <td className="right">£{Math.abs(item.month1Actual).toFixed(2)}</td>
                  <td className="right">£{Math.abs(item.month2Actual).toFixed(2)}</td>
                  <td className="center">
                    {item.month1Actual === 0 && item.month2Actual > 0 ? (
                      <span className={isBad ? 'variance-down' : ''}>Over</span>
                    ) : item.month1Actual > 0 && item.month2Actual === 0 ? (
                      <span className={isGood ? 'variance-up' : ''}>↓ 100.0%</span>
                    ) : item.variance === 0 ? (
                      <span>-</span>
                    ) : (
                      <span className={isGood ? 'variance-up' : isBad ? 'variance-down' : ''}>
                        {item.variance > 0 ? '↑ ' : item.variance < 0 ? '↓ ' : ''}
                        {Math.abs(item.variance).toFixed(1)}%
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
            <tr className="summary-row">
              <td><strong>TOTAL {title.toUpperCase()}</strong></td>
              <td className="right"><strong>£{month1Total.toFixed(2)}</strong></td>
              <td className="right"><strong>£{month2Total.toFixed(2)}</strong></td>
              <td className="center">
                <strong className={isExpense ? (totalVariance < 0 ? 'variance-up' : totalVariance > 0 ? 'variance-down' : '') : (totalVariance > 0 ? 'variance-up' : totalVariance < 0 ? 'variance-down' : '')}>
                  {totalVariance > 0 ? '↑ ' : totalVariance < 0 ? '↓ ' : ''}
                  {Math.abs(totalVariance).toFixed(1)}%
                </strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) {
    return <div className="loading-state">Loading comparison data...</div>;
  }

  return (
    <div className="comparison-view">
      <div className="comparison-header">
        <div className="month-selector">
          <label>Month 1:</label>
          <select value={month1} onChange={(e) => onMonthChange(parseInt(e.target.value), true)}>
            {months.map((m, i) => <option key={i} value={i}>{m} {selectedYear}</option>)}
          </select>
        </div>
        <div className="vs-divider">VS</div>
        <div className="month-selector">
          <label>Month 2:</label>
          <select value={month2} onChange={(e) => onMonthChange(parseInt(e.target.value), false)}>
            {months.map((m, i) => <option key={i} value={i}>{m} {selectedYear}</option>)}
          </select>
        </div>
      </div>
      
      {renderComparisonTable(comparisonData.income, 'Income', 'income')}
      {renderComparisonTable(comparisonData.expense, 'Expenses', 'expense')}
    </div>
  );
}

export default MonthComparisonView;